/**
 * Shared YouTube/Vimeo URL parsing — the ONE place a video URL becomes an
 * embeddable player src.
 *
 * Consumers:
 *  - chat link previews (ChatMessageView's `videoEmbedSrc`, extracted from
 *    there verbatim — its output for every historically-covered input is
 *    pinned byte-identical by videoEmbeds.test.js),
 *  - the documents video node (components/ui/lexicalNodes/DocVideoEmbedNode.js),
 *  - the sanitizer's iframe arm (utils/sanitizeHtml.js), which REBUILDS srcs
 *    from these parsed parts (`parseEmbedSrc` -> `buildEmbedSrc`) rather than
 *    echoing attacker-controlled text.
 *
 * Only two providers exist on purpose: YouTube always embeds via the
 * youtube-nocookie.com domain, Vimeo via player.vimeo.com — the desktop CSP's
 * `frame-src` allowlists exactly those two hosts (guarded by
 * src/tests/mediaCspGuard.test.js), so a third provider added here without a
 * CSP change would render a dead iframe in the packaged app.
 */

// Real YouTube ids are 11 chars of this alphabet; the range is deliberately a
// little loose so a format drift doesn't break parsing, while still rejecting
// URL-encoded junk (%xx), slashes and anything long enough to smuggle text.
export const YOUTUBE_ID_RE = /^[A-Za-z0-9_-]{6,20}$/;
export const VIMEO_ID_RE = /^\d{1,15}$/;

/** 't'/'start' value -> integer seconds >= 0 (0 means "no timestamp").
 *  parseInt on purpose: YouTube's `t=1m30s` form historically parsed to its
 *  leading integer in chat, and that behavior is pinned. Negative/garbage -> 0. */
function normalizeStart(raw) {
  if (raw == null || raw === '') return 0;
  const n = parseInt(raw, 10);
  return Number.isInteger(n) && n > 0 ? n : 0;
}

/**
 * A user-shared video URL -> `{ provider: 'youtube'|'vimeo', id, start }`, or
 * null for anything else. Covers youtube.com/watch?v= | /shorts/ | /embed/,
 * youtube-nocookie.com/embed/, youtu.be/, and vimeo.com/<id>, with `t=`/`start=`
 * timestamps on the youtube.com hosts.
 *
 * NOTE: youtu.be deliberately ignores `?t=` — chat's historical parser never
 * read it there, and chat's LinkPreview output is pinned byte-identical.
 */
export function parseVideoUrl(url) {
  if (typeof url !== 'string') return null;
  let u;
  try {
    u = new URL(url);
  } catch {
    return null;
  }
  // http is accepted (and upgraded — the built embed src is always https);
  // any other scheme that happens to carry a lookalike hostname is not a link.
  if (u.protocol !== 'https:' && u.protocol !== 'http:') return null;
  const host = u.hostname.replace(/^www\.|^m\./, '').toLowerCase();

  if (host === 'youtube.com' || host === 'youtube-nocookie.com') {
    let id = u.searchParams.get('v');
    const shorts = u.pathname.match(/^\/shorts\/([^/]+)/);
    const embed = u.pathname.match(/^\/embed\/([^/]+)/);
    if (!id && shorts) id = shorts[1];
    if (!id && embed) id = embed[1];
    if (!id || !YOUTUBE_ID_RE.test(id)) return null;
    const start = normalizeStart(u.searchParams.get('t') || u.searchParams.get('start'));
    return { provider: 'youtube', id, start };
  }
  if (host === 'youtu.be') {
    const id = u.pathname.slice(1).split('/')[0];
    if (!id || !YOUTUBE_ID_RE.test(id)) return null;
    return { provider: 'youtube', id, start: 0 };
  }
  if (host === 'vimeo.com') {
    const id = u.pathname.split('/').filter(Boolean)[0];
    if (!id || !VIMEO_ID_RE.test(id)) return null;
    return { provider: 'vimeo', id, start: 0 };
  }
  return null;
}

/**
 * Parsed parts -> the canonical embed src, or null if the parts don't
 * validate (defense in depth: this is the LAST hop before an iframe src, so
 * it re-checks the id charset instead of trusting its caller).
 *  - youtube: https://www.youtube-nocookie.com/embed/<id>[?start=N] (N > 0 only)
 *  - vimeo:   https://player.vimeo.com/video/<id>
 */
export function buildEmbedSrc(parts) {
  if (!parts || typeof parts !== 'object') return null;
  const { provider, id, start } = parts;
  if (provider === 'youtube' && YOUTUBE_ID_RE.test(id || '')) {
    const s = Number.isInteger(start) && start > 0 ? `?start=${start}` : '';
    return `https://www.youtube-nocookie.com/embed/${id}${s}`;
  }
  if (provider === 'vimeo' && VIMEO_ID_RE.test(id || '')) {
    return `https://player.vimeo.com/video/${id}`;
  }
  return null;
}

/**
 * Parsed parts -> the canonical WATCH url — what an EXPORT emits in place of a
 * player iframe (a PDF/markdown/print page cannot host an embed, but a link a
 * human clicks can). Watch urls, not embed urls, on purpose: opening
 * `youtube-nocookie.com/embed/<id>` directly shows YouTube's bare player page,
 * while `youtu.be` (and `vimeo.com/<id>`) land on the real watch page.
 *  - youtube: https://youtu.be/<id>[?t=N] (N > 0 only — youtu.be reads `t`)
 *  - vimeo:   https://vimeo.com/<id>
 * Same defense-in-depth as buildEmbedSrc: the id charset is re-checked here
 * because this string ends up as a live link in exported files.
 */
export function videoEmbedWatchUrl(parts) {
  if (!parts || typeof parts !== 'object') return null;
  const { provider, id, start } = parts;
  if (provider === 'youtube' && YOUTUBE_ID_RE.test(id || '')) {
    const s = Number.isInteger(start) && start > 0 ? `?t=${start}` : '';
    return `https://youtu.be/${id}${s}`;
  }
  if (provider === 'vimeo' && VIMEO_ID_RE.test(id || '')) {
    return `https://vimeo.com/${id}`;
  }
  return null;
}

/**
 * An embed IFRAME src -> `{ provider, id, start }`, or null. The read side of
 * `buildEmbedSrc` (round-trips its output exactly), plus one legacy shape:
 * `https://www.youtube.com/embed/<id>` — what the sanitizer allowlisted before
 * this module existed — so old saved documents come back parseable and get
 * REBUILT onto youtube-nocookie by the next save (canonicalization, pinned by
 * sanitizeDocMedia.test.js).
 *
 * https only, exact hosts (no www-stripping here — these are OUR canonical
 * srcs, not user input), exact one-segment paths, `start` read only as an
 * integer query param. Everything else is null and the iframe dies.
 */
export function parseEmbedSrc(src) {
  if (typeof src !== 'string') return null;
  let u;
  try {
    u = new URL(src);
  } catch {
    return null;
  }
  if (u.protocol !== 'https:') return null;
  const host = u.hostname.toLowerCase();

  if (host === 'www.youtube-nocookie.com' || host === 'www.youtube.com') {
    const m = u.pathname.match(/^\/embed\/([^/]+)$/);
    if (!m || !YOUTUBE_ID_RE.test(m[1])) return null;
    return { provider: 'youtube', id: m[1], start: normalizeStart(u.searchParams.get('start')) };
  }
  if (host === 'player.vimeo.com') {
    const m = u.pathname.match(/^\/video\/([^/]+)$/);
    if (!m || !VIMEO_ID_RE.test(m[1])) return null;
    return { provider: 'vimeo', id: m[1], start: 0 };
  }
  return null;
}
